import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { getRefValues } from '../../../shared/services/generalServices';
import RefValuesCategory from '../../../shared/constants/ref-values-category';
import { editEvidenceCorrectionRequest } from '../../services/evidenceService';
import { CORRECTION_STATUS } from '../../constants/correctionStatuses';
import DATE_FORMATS from '../../../shared/constants/dateFormats';
import { formatDate } from '../../../shared/utility/dateFormatter';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSDataGrid } from '../../../shared/components/table';
import RMSChip from '../../../shared/components/chips/RMSChip';
import MarkResolvedDialog from '../add-edit-evidence/MarkedResolvedDialog';
import {
  PORTAL_CORRECTION_TABLE_COLUMN_LABELS,
  PORTAL_CORRECTION_STATUS_CHIP_COLOR,
} from '../../constants/evidencePortalConstants';

const EvidencePortalCorrectionsTable = ({ corrections, onOpenEvidence, refetchCorrections }) => {
  const dispatch = useDispatch();
  const personId = useSelector((state) => state.user?.userData?.user?.ptsPersonID);
  const [correctionStatuses, setCorrectionStatuses] = useState([]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [targetItem, setTargetItem] = useState(null);

  useEffect(() => {
    executeApiAction({
      dispatch,
      action: () => getRefValues(RefValuesCategory.CorrectionRequest),
      onSuccess: setCorrectionStatuses,
    });
  }, [dispatch]);

  const getStatusIdByCode = useCallback(
    (code) => correctionStatuses.find((status) => status.code === code)?.value,
    [correctionStatuses]
  );

  const handleMarkInProgress = async (item) => {
    await executeApiAction({
      dispatch,
      action: () =>
        editEvidenceCorrectionRequest({
          ptsEvidenceCorrectionRequestID: item.ptsEvidenceCorrectionID,
          StatusID: getStatusIdByCode(CORRECTION_STATUS.IN_PROGRESS),
        }),
      onSuccess: () => {
        dispatch(notify('Correction marked as In Progress.', 'success'));
        refetchCorrections();
      },
      onError: (error) => dispatch(notify(error.message, 'error')),
    });
  };

  const handleShowResolve = (item) => {
    setTargetItem(item);
    setResolveDialogOpen(true);
  };

  const handleResolveDialogClose = () => {
    setResolveDialogOpen(false);
    setTargetItem(null);
  };

  const handleSubmitResolve = async (formData) => {
    await executeApiAction({
      dispatch,
      action: () =>
        editEvidenceCorrectionRequest({
          ptsEvidenceCorrectionRequestID: targetItem.ptsEvidenceCorrectionID,
          StatusID: getStatusIdByCode(CORRECTION_STATUS.RESOLVED),
          ResolutionNotes: formData.resolutionNotes,
          ResolvedByPersonID: personId,
          ResolvedAt: new Date(),
        }),
      onSuccess: () => {
        dispatch(notify('Correction resolved successfully.', 'success'));
        refetchCorrections();
      },
      onError: (error) => dispatch(notify(error.message, 'error')),
    });
  };

  const columns = useMemo(
    () => [
      {
        field: 'EvidenceID',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.evidenceId,
        render: (_, row) => (
          <span className="evidence-portal-table__eid">
            {row.EvidenceNumber || `#${row.ptsEvidenceID}`}
          </span>
        ),
      },
      {
        field: 'Incident',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.incident,
        render: (_, row) => row.IncidentNumber || row.IncidentID || '—',
      },
      {
        field: 'Reason',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.reason,
        render: (_, row) => row.CorrectionReason?.Code || '—',
      },
      {
        field: 'Details',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.details,
        render: (_, row) => row.Description || '',
      },
      {
        field: 'Status',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.status,
        render: (_, row) => (
          <RMSChip
            label={row.StatusDetails?.Code || '—'}
            size="small"
            color={PORTAL_CORRECTION_STATUS_CHIP_COLOR[row.StatusDetails?.Code] || 'default'}
          />
        ),
      },
      {
        field: 'Requested',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.requested,
        render: (_, row) => formatDate(row.Created, DATE_FORMATS.MM_DD_YYYY_HH_mm_A),
      },
      {
        field: 'RequestedBy',
        title: PORTAL_CORRECTION_TABLE_COLUMN_LABELS.requestedBy,
        render: (_, row) => row.CreatedBy || '—',
      },
    ],
    []
  );

  const renderActions = (row) => {
    const status = row.StatusDetails?.Code;
    const isOpen = status === CORRECTION_STATUS.OPEN;
    const isInProgress = status === CORRECTION_STATUS.IN_PROGRESS;

    return (
      <div className="evidence-portal-table__actions">
        {isOpen && (
          <RMSButton
            label="In Progress"
            variant="outlined"
            size="small"
            onClick={() => handleMarkInProgress(row)}
          />
        )}
        {(isOpen || isInProgress) && (
          <RMSButton
            label="Resolve"
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleShowResolve(row)}
          />
        )}
      </div>
    );
  };

  if (!corrections.length) {
    return <div className="evidence-portal-empty">No correction requests routed to you.</div>;
  }

  return (
    <div className="evidence-portal-table-wrapper">
      <RMSDataGrid
        primaryKey="ptsEvidenceCorrectionID"
        columns={columns}
        rows={corrections}
        renderActions={renderActions}
        onRowClick={(row) => onOpenEvidence(row.ptsEvidenceID)}
      />

      {resolveDialogOpen && (
        <MarkResolvedDialog
          open={resolveDialogOpen}
          onClose={handleResolveDialogClose}
          onSave={handleSubmitResolve}
        />
      )}
    </div>
  );
};

export default EvidencePortalCorrectionsTable;
